import React, { useEffect, useState } from "react";
import { Avatar, Button, Card, Spin, Timeline, Space, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { Task } from "../../../constants/interface/task";
import {
  useTaskPaymentWithPromoteCode,
  useTaskTraction,
} from "../../../pages/Merchant/ProjectPage/api";
import { useTaskComplianceSummary } from "../../../pages/Merchant/UploadDocument/api";
import { useDebounce } from "../../../utils";

import { SyncOutlined } from "@ant-design/icons";
import { PaymentModal } from "../../../pages/Merchant/ProjectPage/payment-modal";
import { Elements } from "@stripe/react-stripe-js";
import { useFetchPaymentIntent } from "hooks";
import { loadStripe } from "@stripe/stripe-js";
const InfoIcon = styled(InfoCircleOutlined)`
  color: #005eff;
`;

const TargetReviewCompletionDate = styled.div`
  font-weight: bold;
  font-size: 14px;
  display: flex;
  flex-direction: column;
`;

interface TaskDetailCardProps {
  task: Task;
  reload: () => void;
}

export function TaskDetailCard(props: TaskDetailCardProps) {
  const { task } = props;
  let navigate = useNavigate();
  const [paymentVisible, setPaymentVisible] = useState(false);
  const [componentControl, setComponentControl] = useState({
    payment: "",
    uploadDoc: "",
    eSign: "",
  });

  const [param, setParam] = useState({ taskId: task.id });
  const { data: complianceData } = useTaskComplianceSummary(
    useDebounce(param, 200)
  );

  useEffect(() => {
    switch (task.status) {
      case "pending_payment":
        setComponentControl({
          payment: "in_progress",
          uploadDoc: "pending",
          eSign: "pending",
        });
        break;
      case "missing_documents":
        setComponentControl({
          payment: "success",
          uploadDoc: "in_progress",
          eSign: "pending",
        });
        break;
      case "waiting_for_review":
        setComponentControl({
          payment: "success",
          uploadDoc: "success",
          eSign: "pending",
        });
        break;
      case "pending_e_signature":
        setComponentControl({
          payment: "success",
          uploadDoc: "success",
          eSign: "in_progress",
        });
        break;
      case "conformity_certificate_expired":
        setComponentControl({
          payment: "success",
          uploadDoc: "success",
          eSign: "success",
        });
        break;
      case "completed":
        setComponentControl({
          payment: "success",
          uploadDoc: "success",
          eSign: "success",
        });
        break;
      case "rejected":
        setComponentControl({
          payment: "success",
          uploadDoc: "error",
          eSign: "pending",
        });
        break;
    }
  }, [task?.status]);

  const { mutate, isLoading } = useTaskTraction();
  const {
    mutate: mutateTask,
    isLoading: isPaying,
  } = useTaskPaymentWithPromoteCode();
  const handlePayment = async (params: any) => {
    return await mutate({
      taskId: task.id,
      params: {
        ...params,
      },
    });
  };

  const handlePaymentWithCode = async (params: any) => {
    return await mutateTask({
      taskId: task.id,
      params: {
        ...params,
      },
    });
  };

  return (
    <CusCard
      title={<CusCardTitle>Compliance Check</CusCardTitle>}
      extra={
        <TargetReviewCompletionDate>
          <div>
            <Tooltip title="“na” means it is pending supplier’s submission. Target Review Completion Date will be calculated based on submission date.">
              <Space>
                Target Review Completion Date
                <InfoIcon />
              </Space>
            </Tooltip>
          </div>
          <div>{task.estimatedCompletionDate || "N/A"}</div>
        </TargetReviewCompletionDate>
        // <div style={{ fontWeight: "bold", fontSize: 14 }}>
        //   <div>Expected Completion Date</div>
        //   <div>
        //     {task.estimatedCompletionDate
        //       ? task.estimatedCompletionDate
        //       : "N/A"}
        //   </div>
        // </div>
      }
    >
      <h2>Job Progress</h2>
      <Info>Please complete all the tasks below</Info>

      <Timeline style={{ marginTop: 30, marginLeft: 10 }}>
        <Timeline.Item
          color="green"
          dot={
            <Avatar
              style={{
                backgroundColor:
                  componentControl.payment === "success"
                    ? "#B0D400"
                    : "#DADADA",
              }}
              size={28}
              icon={1}
            />
          }
        >
          <div style={{ marginBottom: 20, marginLeft: 18 }}>
            Complete payment for the application
            {componentControl.payment === "in_progress" && (
              <div style={{ marginLeft: 18 }}>
                <Button
                  type="primary"
                  size={`large`}
                  style={{ margin: "20px 0px" }}
                  onClick={() => setPaymentVisible(true)}
                >
                  Proceed with payment
                </Button>
              </div>
            )}
          </div>
        </Timeline.Item>
        <Timeline.Item
          color="green"
          dot={
            <Avatar
              style={{
                backgroundColor:
                  componentControl.uploadDoc === "success"
                    ? "#B0D400"
                    : componentControl.uploadDoc === "error"
                    ? "red"
                    : "#DADADA",
              }}
              size={28}
              icon={2}
            />
          }
        >
          <div style={{ marginBottom: 20, marginLeft: 18 }}>
            Upload required documents
            <div>
              {componentControl.uploadDoc === "error" && (
                <>
                  <Button
                    type="primary"
                    size={`large`}
                    style={{ margin: "20px 0px" }}
                    onClick={() => {
                      navigate(`upload-document/${task.id}`);
                    }}
                  >
                    Re-upload documents
                  </Button>
                  <Info>
                    Note: Please check reviewer’s remark and re-upload the
                    required documents
                  </Info>
                </>
              )}
              {(componentControl.uploadDoc === "in_progress" ||
                componentControl.uploadDoc === "rejected") && (
                <>
                  <Button
                    type="primary"
                    size={`large`}
                    style={{ margin: "20px 0px" }}
                    onClick={() => {
                      navigate(`upload-document/${task.id}`);
                    }}
                  >
                    Upload documents
                  </Button>
                  <Info>
                    {componentControl.uploadDoc === "rejected"
                      ? `Note: Please check reviewer’s remark and re-upload the required documents`
                      : `Note: please click into the documents and re-upload the
                    required documents`}
                  </Info>
                </>
              )}

              {componentControl.uploadDoc === "success" && (
                <>
                  <Button
                    type="primary"
                    ghost
                    size={`large`}
                    style={{ margin: "20px 0px" }}
                    onClick={() => {
                      navigate(`upload-document/${task.id}`);
                    }}
                  >
                    View documents
                  </Button>
                </>
              )}
            </div>
          </div>
        </Timeline.Item>

        {task.status === "waiting_for_review" && (
          <Timeline.Item
            color="green"
            dot={
              <Spin
                style={{ fontSize: 24 }}
                indicator={<SyncOutlined style={{ fontSize: 24 }} spin />}
              />
            }
          >
            <div style={{ marginBottom: 20, marginLeft: 18 }}>
              Waiting for Review
            </div>
          </Timeline.Item>
        )}

        <Timeline.Item
          color="green"
          dot={
            <Avatar
              style={{
                backgroundColor:
                  componentControl.eSign === "success" ? "#B0D400" : "#DADADA",
              }}
              size={28}
              icon={3}
            />
          }
        >
          <div style={{ marginBottom: 20, marginLeft: 18 }}>
            Sign conformity certificate
            {componentControl.eSign === "in_progress" && (
              <div>
                <Button
                  type="primary"
                  size={`large`}
                  style={{ margin: "20px 0px", padding: "0px 30px" }}
                  onClick={() => {
                    navigate(`${task.id}/doc/${complianceData.id}`);
                  }}
                >
                  Sign
                </Button>
              </div>
            )}
            {componentControl.eSign === "success" && (
              <div>
                <Button
                  type="primary"
                  ghost
                  size={`large`}
                  style={{ margin: "20px 0px", padding: "0px 30px" }}
                  onClick={() => {
                    navigate(`${task.id}/doc/${complianceData.id}`);
                  }}
                >
                  View DOC
                </Button>
              </div>
            )}
          </div>
        </Timeline.Item>
      </Timeline>

      <h2 style={{ marginTop: 30 }}>Expiry Date</h2>
      <div>{task.expiryDate ? task.expiryDate : "N/A"}</div>
      <h2 style={{ marginTop: 30 }}>Promote Code</h2>
      <div>{task.promoteCode ? task?.promoteCode : "N/A"}</div>
      {componentControl.payment === "in_progress" && (
        <PaymentModal
          taskId={task.id}
          // clientSecret={paymentIntent.client_secret}
          // paymentDetails={paymentIntent.metadata}
          paymentVisible={paymentVisible}
          setPaymentVisible={setPaymentVisible}
          isLoading={isLoading || isPaying}
          handlePayment={handlePayment}
          handlePaymentPromoteCode={handlePaymentWithCode}
          onPaymentSuccess={() => {
            props.reload();
          }}
        />
      )}
    </CusCard>
  );
}

const CusCard = styled(Card)`
  width: 100%;
  padding: 6px 24px 0px 24px;
  border-radius: 15px;
  position: relative;

  .ant-card-body {
    padding: 20px 0px;
  }

  .ant-card-head {
    padding: 0px 0px;
  }
`;

const Info = styled.div`
  color: #777777;
`;

const CusCardTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.03em;
  text-align: left;
`;
